import { inject, Injectable } from '@angular/core';
import { collection, Firestore, getDocs } from '@angular/fire/firestore';
import { createBatch, deleteBatch, LAYOUT_TEXTS_COLLECTION, SearchResult, updateBatch, WW_WAREHOUSES_COLLECTION } from '@damofire/data-commons';
import { LayoutTextSegment } from '../warehouse/warehouse.dto';
import { generateAndAssignTextIds, WarehouseLayoutTextService } from './warehouse-layout-text.service';
import { AuthDtoSignerService } from '@damofire/data-security';

@Injectable()
export class FirebaseWarehouseLayoutTextService extends WarehouseLayoutTextService {
  private readonly firestore = inject(Firestore);
  private readonly signerService: AuthDtoSignerService = inject(AuthDtoSignerService);

  constructor() {
    super();
  }

  async search(divisionId: string): Promise<SearchResult<LayoutTextSegment>> {
    const collectionRef = collection(this.firestore, `${WW_WAREHOUSES_COLLECTION}/${divisionId}/${LAYOUT_TEXTS_COLLECTION}`);
    const snapshot = await getDocs(collectionRef);
    const items = snapshot.docs.map((doc) => ({ ...(doc.data() as LayoutTextSegment), _id: doc.id }));
    return { result: items, page: { size: items.length } };
  }

  async create(divisionId: string, segments: LayoutTextSegment[]): Promise<void> {
    const textWithIds: LayoutTextSegment[] = generateAndAssignTextIds(segments);

    for (const segment of textWithIds) {
      await this.signerService.sign(segment, true);
    }

    return createBatch(`${WW_WAREHOUSES_COLLECTION}/${divisionId}/${LAYOUT_TEXTS_COLLECTION}`, textWithIds);
  }

  async delete(divisionId: string, segments: LayoutTextSegment[]): Promise<void> {
    return deleteBatch(`${WW_WAREHOUSES_COLLECTION}/${divisionId}/${LAYOUT_TEXTS_COLLECTION}`, segments);
  }

  async update(divisionId: string, segments: LayoutTextSegment[]): Promise<void> {
    for (const segment of segments) {
      await this.signerService.sign(segment, false, false);
    }

    return this.executeUpdateBatch(divisionId, segments);
  }

  protected executeUpdateBatch(divisionId: string, segments: LayoutTextSegment[]): Promise<void> {
    return updateBatch(`${WW_WAREHOUSES_COLLECTION}/${divisionId}/${LAYOUT_TEXTS_COLLECTION}`, segments);
  }
}
